import { useEffect, useState } from 'react';
import { useUserStore } from '../../contexts/userStore';
import './ShiftSelector.css';
import ToolTip from '../Tooltip/Tooltip';
import { MdOutlineDelete } from 'react-icons/md';
import Button from '../Button/Button';
import Select from '../Select/Select';
import ShiftService from '../../services/ShiftService';
import { useAuth } from '../../contexts/auth';
import { LuCheck, LuX } from 'react-icons/lu';
import { v4 as uuidv4 } from "uuid";
import ModalComponent from '../Modal/ModalComponent';
import { UnitsTreeView } from '../UnitsTreeView/UnitsTreeView';
import { BiCollapseAlt } from 'react-icons/bi';

export const ShiftSelector = ({ title, description, onChange, ...props }) => {
	const [modalIsOpen, setModalIsOpen] = useState(false);
	const [shifts, setShifts] = useState([]);
	const [selectedShifts, setSelectedShifts] = useState([]);

	const [modalState, setModalState] = useState();

	const { user } = useAuth();
	const { condominiums, hasHierarchyAccess } = useUserStore();

	async function loadShifts() {
		const response = await ShiftService.getShiftsSummary(user.userData?.selectedOrganizationId);
		if (!response?.data) return;

		const shifts = response.data;
		setShifts(shifts.map(shift => ({
			value: `${shift.id}`,
			text: shift.name,
		})));
	}

	function openUnitModal(id, unitId) {
		setModalIsOpen(true);
		setModalState({ id, unitId });
	}

	function deleteShift(id) {
		setSelectedShifts(prev => {
			const shifts = prev.map((shift) => ({
				...shift,
				delete: shift.id === id ? true : shift.delete,
			}));
			onChange?.(shifts);
			return shifts;
		});
	}

	function addShift() {
		setSelectedShifts(prev => {
			const shifts = [...prev, {
				id: uuidv4(),
				shiftId: null,
				unitId: null,
				delete: false,
				editing: true,
				new: true,
			}];
			onChange?.(shifts);
			return shifts;
		});
	}

	function updateShiftInput(id, field, value) {
		setSelectedShifts(prev => {
			const shifts = [...prev];
			const shiftIdx = shifts.findIndex(shift => shift.id === id);
			shifts[shiftIdx] = { ...shifts[shiftIdx], [field]: value };
			onChange?.(shifts);
			return shifts;
		});
	}

	function cancelEditing(id) {
		setSelectedShifts(prev => {
			const shifts = [...prev];
			const shiftIdx = shifts.findIndex(shift => shift.id === id);
			if (shifts[shiftIdx].new) {
				return shifts.filter(shift => shift.id !== id);
			}
			shifts[shiftIdx] = { ...shifts[shiftIdx], editing: false };
			onChange?.(shifts);
			return shifts;
		});
	}

	function confirmEditing(id) {
		const shift = selectedShifts.find(shift => shift.id === id);
		if (!shift.shiftId) {
			alert("Por favor, um turno deve ser selecionado.");
			return;
		}
		if (!shift.unitId) {
			alert("Por favor, a unidade deve ser selecionada.");
			return;
		}
		setSelectedShifts(prev => {
			const shifts = [...prev];
			const shiftIdx = shifts.findIndex(shift => shift.id === id);
			shifts[shiftIdx] = { ...shifts[shiftIdx], editing: false, new: false };
			onChange?.(shifts);
			return shifts;
		});
	}

	useEffect(() => {
		loadShifts();
	}, [user.userData]);

	useEffect(() => {
		setSelectedShifts(props.shifts)
	}, [props.shifts]);

	return (
		<>
			<div className='shifts-selector'>
				<div className="input-tooltip-container">
					{title ?? 'Turnos'}
					<ToolTip
						tooltipVisible={true}
						tooltipText={description ?? "Vincule turnos e unidades para essa pessoa, o acesso a essas unidades será restringido de acordo com a configuração prévia de horário dos turnos."}
					/>
				</div>
				<div className='selected-shifts-list mb-0!'>
					{selectedShifts.filter(shift => !shift.delete).length > 0 ? (
						<>
							{selectedShifts.filter(shift => !shift.delete && (!shift.new || (shift.new && shift.editing))).map((shift, idx) => (
								<div key={idx} className="selected-shift-item">
									{shift.editing ? (
										<>
											<div className='selected-shift-item-inputs'>
												<Select
													name="shift"
													id="shift"
													value={shift.shiftId}
													onChange={({ target }) => updateShiftInput(shift.id, 'shiftId', target.value)}
													disabled={props.type === "consulta"}
													options={shifts}
													tooltipText="Turno de trabalho da pessoa"
													tooltipVisible={true}
												/>
												<button type='button' className='btn-select-unit' onClick={e => openUnitModal(shift.id, shift.unitId)}>
													<span>
														{condominiums.find(unit => unit.id === shift.unitId)?.name ?? <small>Selecione a unidade</small>}
													</span>
													<BiCollapseAlt />
												</button>
											</div>
											<button type="button" className="btn-confirm-editing" onClick={e => { confirmEditing(shift.id) }}><LuCheck size={18} /></button>
											<button type="button" className="btn-cancel-editing" onClick={e => { cancelEditing(shift.id) }}><LuX size={18} /></button>
										</>
									) : (
										<>
											<div className="selected-shift-item-info">
												<span>
													<b>{shifts.find(_shift => _shift.value === shift.shiftId)?.text}</b>
													{" "}em{" "}{shift.hierarchy ? ' todo(a) ' : ''}
													<i>{condominiums.find(unit => unit.id === shift.unitId)?.name}</i>
												</span>
												<label disabled={!hasHierarchyAccess(shift.unitId)}>
													Toda hierarquia
													<input
														type="checkbox"
														disabled={!hasHierarchyAccess(shift.unitId)}
														checked={shift.hierarchy || false}
														onChange={e => {
															updateShiftInput(shift.id, 'hierarchy', hasHierarchyAccess(shift.unitId) && e.target.checked);
														}}
													/>
												</label>
											</div>
											<button type="button" className="btn-delete-item" onClick={e => { deleteShift(shift.id) }}><MdOutlineDelete size={18} /></button>
										</>
									)}
								</div>
							))}
						</>
					) : (
						<div className="no-levels-selected">Nenhum turno selecionado</div>
					)}
				</div>
				<div className="container-buttons mt-1.5!">
					<Button
						label={"Adicionar turno"}
						action="add"
						onClick={e => addShift()}
						className="button-link"
					/>
				</div>
			</div>
			{
				modalIsOpen && (
					<ModalComponent
						modalIsOpen={modalIsOpen}
						modalText={"Selecione o nível"}
						handleConfirm={() => {
							updateShiftInput(modalState.id, 'unitId', modalState.unitId);
							setModalIsOpen(false);
						}}
						handleCancel={() => { setModalIsOpen(false); }}
						hideButtons={false}
					>
						<div className="modal-levels-selector-content">
							<UnitsTreeView showRoot={false} onChange={ids => setModalState(prev => ({ ...prev, unitId: ids[0] }))} singleCheck={true} />
						</div>
					</ModalComponent>
				)
			}
		</>
	);
}